import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Node, Edge } from 'reactflow';
import { Agent, Model, Project, User, CreateProjectForm, LLMCatalogEntry } from '../types';
import apiService from '../services/api';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /** Tracks whether Zustand persist has completed hydration from localStorage */
  _hasHydrated: boolean;
  /** Set hydration state - called by onRehydrateStorage */
  setHasHydrated: (hydrated: boolean) => void;
  loadUser: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  register: (username: string, password: string, email?: string) => Promise<void>;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      _hasHydrated: false,
      setHasHydrated: (hydrated: boolean) => set({ _hasHydrated: hydrated }),

      loadUser: async () => {
        const currentState = get();
        const token = localStorage.getItem('auth_token');

        // If we already have a valid hydrated state with user, just ensure apiService has the token
        // This prevents E2E tests (which set up Zustand state directly) from having their state
        // overwritten by a potentially failing API validation call
        if (currentState._hasHydrated && currentState.isAuthenticated && currentState.user && token) {
          apiService.setToken(token);
          set({ isLoading: false });
          return;
        }

        set({ isLoading: true });
        if (token) {
          apiService.setToken(token);
          try {
            const user = await apiService.getCurrentUser();
            set({ user, isAuthenticated: true, isLoading: false });
          } catch (error) {
            apiService.clearTokens();
            set({ user: null, isAuthenticated: false, isLoading: false });
          }
        } else {
          set({ user: null, isAuthenticated: false, isLoading: false });
        }
      },

      login: async (email: string, password: string) => {
        set({ isLoading: true });
        try {
          const result = await apiService.login(email, password);
          // Tokens are already set by the apiService.login method
          const user = await apiService.getCurrentUser();
          set({ user, isAuthenticated: true, isLoading: false });
        } catch (error) {
          apiService.clearTokens();
          set({ user: null, isAuthenticated: false, isLoading: false });
          throw error;
        }
      },

      register: async (username: string, password: string, email?: string) => {
        set({ isLoading: true });
        try {
          const result = await apiService.register(username, password, email);
          // Tokens are already set by the apiService.register method
          const user = await apiService.getCurrentUser();
          set({ user, isAuthenticated: true, isLoading: false });
        } catch (error) {
          apiService.clearTokens();
          set({ user: null, isAuthenticated: false, isLoading: false });
          throw error;
        }
      },

      logout: () => {
        apiService.clearTokens();
        set({ user: null, isAuthenticated: false, isLoading: false });
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ user: state.user, isAuthenticated: state.isAuthenticated }),
      onRehydrateStorage: () => (state) => {
        // Called after Zustand persist has completed hydrating from localStorage
        // This allows components to wait for hydration before making auth decisions
        state?.setHasHydrated(true);
      },
    }
  )
);

// --- FLOW STORE ---
//
// AC-1377 P5.2 (AC-1413) — Post-epic audit. After P2.4 (AC-1401),
// P3.5 (AC-1406), P5.1 (AC-1412), and the AC-1411 page migrations, this
// store owns ONLY ephemeral, client-only canvas state. There is NO
// server-derived field on `FlowState` and NO `apiService` import inside
// the flow-store-related code path. Every server resource the editor
// needs (workflow detail, steps, execution snapshot, agents, models,
// registries, all-workflows) flows through a `useQuery` hook under
// `src/hooks/queries/`, and every server write flows through one of the
// mutation hooks in the same directory whose `onSuccess` invalidates the
// matching RQ key per Technical Design section 2.6
// (`docs/technical/AC-1377-react-query-technical-design.md`).
//
// ## What lives here (and why)
//
//   - `nodes`, `edges` — React Flow's locally-mutable canvas arrays.
//     Drag, node-resize, and intermediate edge edits write to these on
//     every frame; the server is not involved until the user commits via
//     a mutation hook (e.g. `useUpdateStepPosition`,
//     `useStepMutations.update`). The seed for these arrays comes from
//     `useWorkflow(id)` + `useWorkflowSteps(id)` via the pure
//     `buildFlowGraph` helper, but once seeded the canvas owns its own
//     state — RQ does not "push" updates into here.
//
//   - `selectedNode`, `selectedEdge` — selection is a UI concept (which
//     element has focus right now). It is not server data and never will
//     be. Lives here forever.
//
//   - `validationErrors` — computed from the *current canvas state* (not
//     from the server). The validator runs client-side over `nodes` /
//     `edges` so users see issues live as they edit. Backend re-validates
//     on save, but those errors flow through the relevant mutation's
//     `onError` toast, not into this slice.
//
//   - `isModelModalOpen` — a modal-open flag. Pure UI.
//
// ## What was removed (and why each was server state in disguise)
//
//   - `loadedWorkflowId` (AC-1401, P2.4) — was the AC-1378 cache-key
//     workaround for "remember which workflow's server data is in the
//     store right now so we can short-circuit refetch". Pure server-state
//     identity. RQ owns cache identity now.
//
//   - `loadWorkflow()` body (AC-1401, P2.4) — was a fetch +
//     transform-and-store for `getWorkflow` + `getWorkflowSteps`. Server
//     state. Replaced by `useWorkflow` / `useWorkflowSteps`.
//
//   - The stale-fetch guard (AC-1401, P2.4) — was AC-1386's defense
//     against an in-flight fetch from workflow A landing in the store
//     while the user navigated to workflow B. RQ's per-key request scope
//     makes this impossible by construction.
//
//   - The cache-skip block (AC-1401, P2.4) — was AC-1378's "if we
//     already have this workflow's data, don't re-fetch". RQ's
//     stale-while-revalidate + dedup gives the same UX without a manual
//     comparison.
//
//   - `loadWorkflow(workflowId)` invalidation shim (AC-1406, P3.5) —
//     was a transitional seam during P3.1-P3.4 that translated old
//     "reload after write" calls into RQ invalidations. With every
//     caller migrated to mutation hooks, the shim was dead code. Removed
//     so new callers cannot regress to the stale-modal-data class of
//     bug.
//
//   - `isLoading` field (AC-1413, P5.2) — was set by the legacy
//     `loadWorkflow` body when it transitioned through "fetching" /
//     "complete" states. Once `loadWorkflow` was deleted in AC-1401
//     nothing wrote to `isLoading` anymore, but a few legacy consumer
//     branches in `WorkflowEditor.tsx` (the seed-undo-history effect,
//     the node-click guard, the "Loading workflow..." render path) still
//     read it. Those branches were dead — `isLoading` was permanently
//     `false`. The audit replaces them with the equivalent reads from
//     `useWorkflow`/`useWorkflowSteps`'s native `isLoading` so the
//     loading-state semantics are now anchored to RQ, where the
//     server-state actually lives. With no consumer left, the
//     `isLoading` slot on FlowState is removed.
//
// ## What remained intentional
//
//   - `clear()` is a selection / validation / modal reset only. It does
//     NOT clobber nodes/edges — those belong to the canvas while the
//     user is still on the page. (Switching workflows triggers a remount
//     and the seed effect rebuilds nodes/edges from the new
//     `useWorkflow` query.)
interface FlowState {
  nodes: Node[];
  edges: Edge[];
  selectedNode: string | null;
  selectedEdge: string | null;
  validationErrors: Array<{ message: string; type: string; nodeId?: string }>;
  setNodes: (nodes: Node[] | ((nodes: Node[]) => Node[])) => void;
  setEdges: (edges: Edge[]) => void;
  setSelectedNode: (nodeId: string | null) => void;
  setSelectedEdge: (edgeId: string | null) => void;
  setValidationErrors: (errors: Array<{ message: string; type: string; nodeId?: string }>) => void;
  addNode: (node: Node) => void;
  addEdge: (edge: Edge) => void;
  clear: () => void;
  isModelModalOpen: boolean;
  setIsModelModalOpen: (open: boolean) => void;
}

export const useFlowStore = create<FlowState>((set) => ({
  nodes: [],
  edges: [],
  selectedNode: null,
  selectedEdge: null,
  validationErrors: [],
  isModelModalOpen: false,
  setIsModelModalOpen: (open) => set({ isModelModalOpen: open }),
  setNodes: (payload) => set((state) => ({ nodes: typeof payload === 'function' ? payload(state.nodes) : payload })),
  setEdges: (edges) => set({ edges }),
  setSelectedNode: (nodeId) => set({ selectedNode: nodeId }),
  setSelectedEdge: (edgeId) => set({ selectedEdge: edgeId }),
  setValidationErrors: (errors) => set({ validationErrors: errors }),
  addNode: (node) => set((state) => ({ nodes: [...state.nodes, node] })),
  addEdge: (edge) => set((state) => ({ edges: [...state.edges, edge] })),
  clear: () => set({ selectedNode: null, selectedEdge: null, validationErrors: [], isModelModalOpen: false }),
}));

// --- PROJECT STORE ---
//
// AC-1412 / AC-1377 P5.1: Server-state slices were removed from this store.
// What was deleted in this ticket:
//   - `agents`, `models`, `allWorkflows` array slices
//   - `loadAgents(projectId)`, `loadModels(projectId)`, `loadAllWorkflows()`
//   - The agent / model / workflow CRUD dual-write shims:
//     `createAgent` / `updateAgent` / `deleteAgent`
//     `createModel` / `updateModel` / `deleteModel`
//     `createWorkflow` / `updateWorkflow` / `deleteWorkflow`
//
// Project-scoped reads now go through React Query hooks
// (`useAgents(projectId)`, `useModels(projectId)`, `useAllWorkflows()`),
// and project-scoped writes go through dedicated mutation hooks
// (`useAgentMutations`, `useModelMutations`, `useWorkflowMutations`). The
// mutation hooks own invalidation of the relevant cache keys per TD
// section 2.6, so there is no longer a manual reload step after a write.
//
// Intentionally kept in this store (per Phase 5 design):
//   - `currentProject`, `projects`, `loadProjects`, `setCurrentOperation`
//   - `createProject` / `updateProject` / `deleteProject`
//   - `operationModalOpen` (and its open/close actions)
//   - `allAgents`, `allModels`, `loadAllAgents`, `loadAllModels` (read by
//     Dashboard.tsx; not part of this ticket's deletion target)
//   - `llmCatalog` / `loadLLMCatalog` (read by App.tsx, AgentFormModal,
//     InstructionAssistantPanel; not part of this ticket's deletion target)
interface OperationState {
  projects: Project[];
  currentProject: Project | null;
  allAgents: Agent[];
  allModels: Model[];
  llmCatalog: LLMCatalogEntry[];
  llmCatalogLoading: boolean;
  /**
   * AC-1493 P6: BYOK mode flag from the LLM catalog response. When `true`,
   * UI surfaces (AgentFormModal picker, OrchestrationEditor banner) should
   * gate behavior on per-model `available` flags / configured user keys.
   * Defaults to `false` until `loadLLMCatalog()` resolves.
   */
  llmByokMode: boolean;
  operationModalOpen: boolean;
  openOperationModal: () => void;
  closeOperationModal: () => void;
  loadProjects: () => Promise<void>;
  loadAllAgents: () => Promise<void>;
  loadAllModels: () => Promise<void>;
  loadLLMCatalog: () => Promise<void>;
  setCurrentOperation: (project: Project | null) => void;
  createProject: (data: CreateProjectForm) => Promise<{ success: true; project: Project } | { success: false; error: string }>;
  updateProject: (projectId: string, data: Partial<CreateProjectForm> & { is_public?: boolean; visibility?: string; team_ids?: number[] }) => Promise<Project | null>;
  deleteProject: (projectId: string) => Promise<void>;
}

export const useProjectStore = create<OperationState>((set, get) => ({
  projects: [],
  currentProject: null,
  allAgents: [],
  allModels: [],
  llmCatalog: [],
  llmCatalogLoading: false,
  llmByokMode: false,
  operationModalOpen: false,
  openOperationModal: () => set({ operationModalOpen: true }),
  closeOperationModal: () => set({ operationModalOpen: false }),
  loadProjects: async () => {
    try {
      const projects = await apiService.getProjects();
      set({ projects });
    } catch (error) {
      set({ projects: [] });
    }
  },
  setCurrentOperation: (project) => {
    set({ currentProject: project });
    apiService.setCurrentOperationId(project?.project_id ?? null);
  },
  loadAllAgents: async () => {
    try {
      const allAgents = await apiService.getAllAgents();
      set({ allAgents });
    } catch (error) {
      set({ allAgents: [] });
    }
  },
  loadAllModels: async () => {
    try {
      const allModels = await apiService.getAllModels();
      set({ allModels });
    } catch (error) {
      set({ allModels: [] });
    }
  },
  loadLLMCatalog: async () => {
    set({ llmCatalogLoading: true });
    try {
      const response = await apiService.getLLMCatalog();
      set({
        llmCatalog: response.models,
        llmByokMode: Boolean(response.byok_mode),
        llmCatalogLoading: false,
      });
    } catch (error) {
      set({ llmCatalog: [], llmByokMode: false, llmCatalogLoading: false });
    }
  },
  createProject: async (data) => {
    try {
      const project = await apiService.createProject({
        name: data.name,
        description: data.description ?? ''
      });
      set({ projects: [...get().projects, project] });
      return { success: true, project };
    } catch (error: any) {
      // Extract error message from API response
      const errorMessage = error?.response?.data?.detail
        || error?.message
        || 'Failed to create project.';
      return { success: false, error: errorMessage };
    }
  },
  updateProject: async (projectId, data) => {
    try {
      const updated = await apiService.updateProject(projectId, data);
      set({
        projects: get().projects.map((p) => (String(p.project_id) === String(projectId) ? updated : p)),
        currentProject: String(get().currentProject?.project_id) === String(projectId) ? updated : get().currentProject
      });
      return updated;
    } catch (error) {
      return null;
    }
  },
  deleteProject: async (projectId: string) => {
    try {
      await apiService.deleteProject(projectId);
      set(state => ({
        projects: state.projects.filter(op => op.project_id !== projectId),
        currentProject: state.currentProject?.project_id === projectId ? null : state.currentProject
      }));
    } catch (error) {
      console.error('Error deleting project:', error);
      throw error;
    }
  },
}));

// --- UI STORE ---
interface LastWorkflow {
  workflowId: string;
  name: string;
  projectId: string;
}

interface UIState {
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  isMobileDrawerOpen: boolean;
  setMobileDrawerOpen: (open: boolean) => void;
  // AC-1699: open-state of the global Agent Commander assistant panel. Lifted
  // out of Layout-local state so any page (command center, project dashboard,
  // workflow editor) can launch the copilot programmatically. Persisted so it
  // stays open across navigation; Layout guards it closed on mobile at mount.
  assistantOpen: boolean;
  setAssistantOpen: (open: boolean) => void;
  openAssistant: () => void;
  closeAssistant: () => void;
  toggleAssistant: () => void;
  // AC-1705: user-chosen width (px) of the assistant panel. Drag-resizable and
  // maximizable; persisted. Layout clamps it to [min, viewport - sidebar].
  assistantWidth: number;
  setAssistantWidth: (width: number) => void;
  componentLibraryOpen: boolean;
  setComponentLibraryOpen: (open: boolean) => void;
  propertiesPanelOpen: boolean;
  setPropertiesPanelOpen: (open: boolean) => void;
  currentView: string;
  setCurrentView: (view: string) => void;
  // Track last visited workflow for contextual back navigation
  lastWorkflow: LastWorkflow | null;
  setLastWorkflow: (workflowId: string, name: string, projectId: string) => void;
  clearLastWorkflow: () => void;
  // Sidebar group expand/collapse state
  sidebarGroups: Record<string, boolean>;
  toggleSidebarGroup: (groupId: string) => void;
}

// Export help store
export { useHelpStore } from './helpStore';

// Export tutorial store
export { useTutorialStore } from './tutorialStore';

// Export theme store
export { useThemeStore } from './themeStore';

export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      sidebarOpen: true,
      setSidebarOpen: (open) => set({ sidebarOpen: open }),
      isMobileDrawerOpen: false,
      setMobileDrawerOpen: (open) => set({ isMobileDrawerOpen: open }),
      // AC-1699: Agent Commander assistant panel open-state + actions.
      assistantOpen: false,
      setAssistantOpen: (open) => set({ assistantOpen: open }),
      openAssistant: () => set({ assistantOpen: true }),
      closeAssistant: () => set({ assistantOpen: false }),
      toggleAssistant: () => set((state) => ({ assistantOpen: !state.assistantOpen })),
      // AC-1705: default 384px (matches the prior w-96 fixed width).
      assistantWidth: 384,
      setAssistantWidth: (width) => set({ assistantWidth: width }),
      componentLibraryOpen: false,
      setComponentLibraryOpen: (open) => set({ componentLibraryOpen: open }),
      propertiesPanelOpen: false,
      setPropertiesPanelOpen: (open) => set({ propertiesPanelOpen: open }),
      currentView: 'dashboard',
      setCurrentView: (view) => set({ currentView: view }),
      // Last workflow tracking
      lastWorkflow: null,
      setLastWorkflow: (workflowId, name, projectId) =>
        set({ lastWorkflow: { workflowId, name, projectId } }),
      clearLastWorkflow: () => set({ lastWorkflow: null }),
      // Sidebar group expand/collapse state (persisted)
      sidebarGroups: { project: true, config: false, community: false, learn: false },
      toggleSidebarGroup: (groupId) =>
        set((state) => ({
          sidebarGroups: {
            ...state.sidebarGroups,
            [groupId]: !state.sidebarGroups[groupId],
          },
        })),
    }),
    {
      name: 'ui-storage',
      partialize: (state) => ({
        lastWorkflow: state.lastWorkflow,
        sidebarGroups: state.sidebarGroups,
        // AC-1699: persist the copilot's open-state so it survives a reload.
        // Layout closes it on mount when the viewport is mobile.
        assistantOpen: state.assistantOpen,
        // AC-1705: remember the user's chosen panel width.
        assistantWidth: state.assistantWidth,
      }),
    }
  )
);